import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { isNetworkError, trackingApi, type LivePosition, type LivePositionsResponse } from '@/api/client';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/Button';
import { StatusBadge } from '@/components/StatusBadge';
import { internalNavItems } from '@/pages/navItems';
import { formatAgo, formatDateTime } from '@/lib/format';
import { formatLoadError } from '@/hooks';

const REFRESH_MS = 30_000;
const DEFAULT_CENTER: L.LatLngExpression = [14.55, 121.0];
const DEFAULT_ZOOM = 10;

function keyOf(p: LivePosition): string {
  return p.vehicleId ?? `person:${p.personId}`;
}

function truckIcon(p: LivePosition): L.DivIcon {
  const label = p.plateNumber ?? (p.driverName ? p.driverName.split(' ')[0] : 'Crew');
  const el = document.createElement('div');
  el.className = `truck-pin${p.stale ? ' stale' : ''}`;
  const dot = document.createElement('span');
  dot.className = 'truck-dot';
  const tag = document.createElement('span');
  tag.className = 'truck-tag';
  tag.textContent = label;
  el.append(dot, tag);
  return L.divIcon({ html: el, className: 'truck-pin-wrap', iconSize: [16, 16], iconAnchor: [8, 8], popupAnchor: [0, -10] });
}

function popupContent(p: LivePosition, onOpenTrip: (tripId: string) => void): HTMLElement {
  const box = document.createElement('div');
  box.className = 'truck-popup';
  const title = document.createElement('strong');
  title.textContent = p.plateNumber ?? 'No truck linked';
  box.append(title);
  const line = (label: string, value: string) => {
    const row = document.createElement('div');
    const l = document.createElement('span');
    l.className = 'muted';
    l.textContent = `${label}: `;
    const v = document.createElement('span');
    v.textContent = value;
    row.append(l, v);
    box.append(row);
  };
  line('Driver', p.driverName ?? '—');
  line('Trip', p.tripNumber ?? 'Not on a trip');
  line('Last seen', `${formatAgo(p.ageSeconds)}${p.stale ? ' (no recent signal)' : ''}`);
  if (p.accuracyM != null) line('Accuracy', `about ${Math.round(p.accuracyM)} m`);
  if (p.batteryPct != null) line('Phone battery', `${Math.round(p.batteryPct)}%`);
  if (p.tripId) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'popup-link';
    btn.textContent = 'Open trip';
    btn.onclick = () => onOpenTrip(p.tripId!);
    box.append(btn);
  }
  return box;
}

export function LiveMap() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const fittedRef = useRef(false);
  const [data, setData] = useState<LivePositionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);

  const openTrip = useCallback((tripId: string) => navigate(`/app/dispatch/trips/${tripId}`), [navigate]);

  const load = useCallback(async () => {
    try {
      const res = await trackingApi.livePositions();
      setData(res);
      setError(null);
    } catch (err) {
      setError(formatLoadError(err, isNetworkError(err) || !navigator.onLine));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  // Map: created once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    const markers = markersRef.current;
    setTimeout(() => map.invalidateSize(), 50);
    return () => {
      map.remove();
      mapRef.current = null;
      markers.clear();
    };
  }, []);

  // Markers: add / move / remove on each refresh.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data) return;
    const seen = new Set<string>();
    for (const p of data.positions) {
      const key = keyOf(p);
      seen.add(key);
      const existing = markersRef.current.get(key);
      const content = popupContent(p, openTrip);
      if (existing) {
        existing.setLatLng([p.latitude, p.longitude]);
        existing.setIcon(truckIcon(p));
        existing.setPopupContent(content);
      } else {
        const m = L.marker([p.latitude, p.longitude], { icon: truckIcon(p) }).bindPopup(content).addTo(map);
        markersRef.current.set(key, m);
      }
    }
    for (const [key, m] of markersRef.current) {
      if (!seen.has(key)) {
        m.remove();
        markersRef.current.delete(key);
      }
    }
    if (!fittedRef.current && data.positions.length) {
      const bounds = L.latLngBounds(data.positions.map((p) => [p.latitude, p.longitude] as [number, number]));
      map.fitBounds(bounds.pad(0.2), { maxZoom: 14 });
      fittedRef.current = true;
    }
  }, [data, openTrip]);

  const focus = (p: LivePosition) => {
    const key = keyOf(p);
    setSelected(key);
    const map = mapRef.current;
    const marker = markersRef.current.get(key);
    if (map && marker) {
      map.setView(marker.getLatLng(), Math.max(map.getZoom(), 14));
      marker.openPopup();
    }
  };

  const positions = data?.positions ?? [];
  const live = positions.filter((p) => !p.stale).length;

  return (
    <Layout navItems={internalNavItems} fullWidth>
      <div className="page live-page">
        <div className="page-header row-between">
          <div>
            <h1>Live map</h1>
            <p className="text-muted">
              Where trucks are now, from the crew app. Updates every 30 seconds.
              {data ? ` Last updated ${formatDateTime(data.generatedAt)}.` : ''}
            </p>
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={() => void load()}>
            Refresh now
          </Button>
        </div>

        {data && !data.trackingEnabled && (
          <p className="text-warning">
            Location sharing is turned off in Settings, so the crew app is not sending truck locations.
          </p>
        )}
        {error && (
          <p className="text-urgent" role="alert">
            {error}{' '}
            <Button type="button" size="sm" variant="ghost" onClick={() => void load()}>Retry</Button>
          </p>
        )}

        <div className="live-layout">
          <div ref={containerRef} className="live-map" />
          <aside className="live-list" aria-label="Trucks">
            <div className="live-list-head">
              <strong>{positions.length} truck{positions.length === 1 ? '' : 's'}</strong>
              <span className="text-muted">{live} with a recent signal</span>
            </div>
            {loading && !data ? (
              <div className="loading-center"><div className="spinner" /></div>
            ) : positions.length === 0 ? (
              <p className="text-muted live-empty">
                No trucks have sent a location in the last 12 hours. Trucks appear here when crew are clocked in or on a
                trip with the crew app open.
              </p>
            ) : (
              <ul>
                {positions.map((p) => {
                  const key = keyOf(p);
                  return (
                    <li key={key} className={`${p.stale ? 'stale' : ''} ${selected === key ? 'selected' : ''}`}>
                      <button type="button" className="live-row" onClick={() => focus(p)}>
                        <span className="live-row-top">
                          <strong>{p.plateNumber ?? 'No truck linked'}</strong>
                          <span className={p.stale ? 'text-warning' : 'text-muted'}>{formatAgo(p.ageSeconds)}</span>
                        </span>
                        <span className="text-muted">{p.driverName ?? '—'}</span>
                        <span className="live-row-trip">
                          {p.tripNumber ? (
                            <>
                              {p.tripNumber}
                              {p.tripStatus && <StatusBadge status={p.tripStatus} size="sm" />}
                            </>
                          ) : (
                            <span className="text-muted">Not on a trip</span>
                          )}
                        </span>
                        {p.stale && <span className="text-warning live-stale">No recent signal</span>}
                      </button>
                      {p.tripId && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => openTrip(p.tripId!)}>
                          Open trip
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>
        </div>

        <style>{`
          .live-layout {
            display: grid;
            grid-template-columns: 1fr 320px;
            gap: var(--space-md);
            align-items: stretch;
          }
          @media (max-width: 900px) {
            .live-layout { grid-template-columns: 1fr; }
          }
          .live-map {
            height: calc(100dvh - 220px);
            min-height: 420px;
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            z-index: 0;
          }
          .live-list {
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            overflow-y: auto;
            max-height: calc(100dvh - 220px);
            min-height: 200px;
          }
          .live-list-head {
            display: flex;
            justify-content: space-between;
            gap: var(--space-sm);
            padding: var(--space-md);
            border-bottom: 1px solid var(--color-border);
            font-size: 0.875rem;
          }
          .live-list ul { list-style: none; margin: 0; padding: 0; }
          .live-list li {
            display: flex;
            align-items: flex-start;
            gap: var(--space-xs);
            padding: var(--space-sm) var(--space-md);
            border-bottom: 1px solid var(--color-border);
          }
          .live-list li.selected { background: var(--color-sand); }
          .live-list li.stale { opacity: 0.75; }
          .live-row {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 2px;
            text-align: left;
            background: none;
            border: none;
            padding: 0;
            cursor: pointer;
            font: inherit;
            font-size: 0.875rem;
            color: inherit;
          }
          .live-row-top { display: flex; justify-content: space-between; gap: var(--space-sm); }
          .live-row-trip { display: flex; align-items: center; gap: var(--space-xs); }
          .live-stale { font-size: 0.75rem; }
          .live-empty { padding: var(--space-md); font-size: 0.875rem; }
          .truck-pin-wrap { background: none; border: none; }
          .truck-pin { position: relative; }
          .truck-dot {
            display: block;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: var(--color-signal, #FFD100);
            border: 3px solid var(--color-ink, #0E1216);
            box-shadow: 0 1px 4px rgba(0,0,0,0.35);
          }
          .truck-tag {
            position: absolute;
            left: 20px;
            top: -2px;
            white-space: nowrap;
            background: var(--color-ink, #0E1216);
            color: #fff;
            font-size: 11px;
            font-weight: 600;
            padding: 1px 6px;
            border-radius: 4px;
          }
          .truck-pin.stale .truck-dot { background: #B8BEC4; border-color: #6B7480; }
          .truck-pin.stale .truck-tag { background: #6B7480; }
          .truck-popup { display: flex; flex-direction: column; gap: 2px; font-size: 13px; min-width: 180px; }
          .truck-popup strong { font-size: 14px; margin-bottom: 2px; }
          .truck-popup .muted { color: #6B7480; }
          .popup-link {
            margin-top: 6px;
            align-self: flex-start;
            background: none;
            border: none;
            padding: 0;
            color: inherit;
            text-decoration: underline;
            cursor: pointer;
            font: inherit;
            font-weight: 600;
          }
        `}</style>
      </div>
    </Layout>
  );
}
